import {getTestProps, type DataTestAttrs} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {getToggleButtonRoleProps, type ToggleButtonRoleProps} from 'lib/a11y/ButtonA11y';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_POWDER_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {isEnterKey, isSpaceKey} from 'util/Keyboard';

import VersionHistoryItemActionMenu, {
  VersionHistoryItemActionMenuProps,
} from './VersionHistoryItemActionMenu';

export interface VersionHistoryItemProps extends VersionHistoryItemActionMenuProps {
  rows: Array<JSX.Element>;
  isSelected: boolean;
  onClick?: () => void;
  dataTestAttrs?: DataTestAttrs;
}

function VersionHistoryItemBase({
  rows,
  isSelected,
  onClick,
  firstOption,
  secondOption,
  dataTestAttrs,
}: VersionHistoryItemProps): JSX.Element {
  const shouldShowActionButton = isSelected && (firstOption.onClick || secondOption.onClick);
  const actionMenu = <VersionHistoryItemActionMenu firstOption={firstOption} secondOption={secondOption} />;
  const {onKeyDown: onRowsKeyDown, ...rowsButtonProps}: Partial<ToggleButtonRoleProps> =
    onClick != null
      ? getToggleButtonRoleProps({onPress: onClick, pressed: isSelected, stopPropagation: true})
      : {};
  return (
    <div
      className={classNames(
        'version-history-item',
        css(styles.container, isSelected && styles.containerIsSelected, onClick && styles.containerClickable)
      )}
      onClick={onClick}
      {...getTestProps(dataTestAttrs ?? {component: 'VersionHistoryItemBase', element: 'root'})}
    >
      {/* The rows select the item from the keyboard too, and say whether it's selected; the action menu stays a
          separate button. A click in the item's padding still selects it through the container. */}
      <div
        className={css(styles.containerContent)}
        {...rowsButtonProps}
        onKeyDown={
          onRowsKeyDown &&
          ((e) => {
            // Keys pressed on a link in a row (e.g. an agent attribution) belong to the link.
            if (e.target !== e.currentTarget) {
              return;
            }
            // Keep Space from also scrolling the version history panel.
            if (isEnterKey(e) || isSpaceKey(e)) {
              e.preventDefault();
            }
            onRowsKeyDown(e);
          })
        }
        {...getTestProps({component: 'VersionHistoryItemBase', element: 'selectButton'})}
      >
        {rows.map((row, index) => (
          <div
            key={`version-history-item-row-${index}`}
            className={classNames('version-history-item-row')}
            {...getTestProps({component: 'VersionHistoryItemBase', element: 'row', index: `${index}`})}
          >
            {row}
          </div>
        ))}
      </div>
      <div className={css(styles.containerActionButton)}>
        {shouldShowActionButton && (
          <Dropdown closeOnInsideClick={true} menu={actionMenu}>
            <Button
              size={ButtonSize.MINI}
              buttonStyle={ButtonStyle.TERTIARY_WHITE_TRANSPARENT}
              dataTestAttrs={{component: 'version-history-item', element: 'action-button'}}
              className={css(styles.actionButton)}
              aria-label="Open version history item actions"
            >
              <BenchlingIcon icon="ellipsis-vertical" />
            </Button>
          </Dropdown>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    ':hover': {
      backgroundColor: Color.GRAY_50,
    },
    padding: '10px 15px 10px 10px',
    display: 'flex',
    flexDirection: 'row',
    gap: '5px',
  },
  containerIsSelected: {
    backgroundColor: BLUE_POWDER_LIGHT,
    ':hover': {
      backgroundColor: BLUE_POWDER_LIGHT,
    },
  },
  containerClickable: {
    cursor: 'pointer',
  },
  containerContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  containerActionButton: {
    flex: 0,
    marginLeft: 'auto',
  },
  actionButton: {
    padding: '6px',
    border: 'none',
    color: GRAY_SEMIDARK,
  },
});

export default VersionHistoryItemBase;
